import React from 'react';
import Highlighter from 'react-highlight-words';
import { isAulaEditorState } from '@ublend-npm/aula-editor-utils';
import { Box } from '@ublend-npm/aulaui-core';

import EditorReadOnly from '../common/EditorPlus/EditorReadOnly.container';

const highlightMatch = (text: string) => {
  const pattern = /<em>.*?<\/em>/g;
  const matches = text.match(pattern);

  return (
    <Highlighter
      searchWords={matches || []}
      autoEscape
      textToHighlight={text}
      highlightTag={({ children }) => (
        <mark
          key={children}
          style={{
            color: '#ffffff',
            backgroundColor: '#746887',
          }}
        >
          {children.replace(/<em>/g, '').replace(/<\/em>/g, '')}
        </mark>
      )}
    />
  );
};

type OmniSearchResultPreviewProps = {
  content: any; // Aula editor or TH90
  keysHighlightMap: Array<Array<string>>;
};

export const OmniSearchResultPreview = ({
  content,
  keysHighlightMap,
}: OmniSearchResultPreviewProps) => {
  const isAulaEditor: boolean = isAulaEditorState(content);
  const highlightedTexts = Object.entries<string[]>(keysHighlightMap);
  const withHighlightedText: boolean = highlightedTexts.length > 0;

  if (isAulaEditor) {
    if (withHighlightedText) {
      return highlightedTexts.map(([key, texts], i) => {
        const block = content.value?.[key];
        // render text as text, highlighting matches
        if (block && (!block.type || block.type.includes('list'))) {
          return (
            <Box key={key}>
              {highlightMatch(`${i === 0 ? '...' : ''} ${texts.join('')} ...`)}
            </Box>
          );
        }
        // render rest using the editor
        return (
          <EditorReadOnly
            rawEditorState={{
              ...content,
              value: [block],
            }}
          />
        );
      });
    } else {
      // no text to highlight, render only first block
      const firstBlock = content.value[0];
      // render text as text
      const text = firstBlock?.children?.[0]?.text;
      if (text) {
        return `${text.substring(0, 150)} ...`;
      }
      // render rest using the editor
      return (
        <EditorReadOnly
          rawEditorState={{
            ...content,
            value: [firstBlock],
          }}
        />
      );
    }
  } else {
    if (withHighlightedText) {
      return content.blocks
        .filter((block) => block.key in keysHighlightMap)
        .map((block, i) => {
          // render text as text, highlighting matches
          if (block.text && block.text.trim().length) {
            return (
              <Box sx={{}} key={block.key}>
                {highlightMatch(
                  `${i === 0 ? '...' : ''} ${keysHighlightMap[block.key]} ...`
                )}
              </Box>
            );
          }
          // render rest using the editor
          return (
            <EditorReadOnly
              key={block.key}
              rawEditorState={{
                ...content,
                blocks: [block],
              }}
            />
          );
        });
    } else {
      // no text to highlight, render only first block
      const firstBlock = content.blocks[0];
      // render text as text
      if (firstBlock.text) {
        return `${firstBlock.text.substring(0, 150)} ...`;
      }
      // render rest using the editor
      return (
        <EditorReadOnly
          key={firstBlock.key}
          rawEditorState={{
            ...content,
            blocks: [firstBlock],
          }}
        />
      );
    }
  }
};

OmniSearchResultPreview.displayName = 'OmniSearchResultPreview';
